import { useState } from 'react';
import { BaseModal } from '@/components/shared';
import type { StatusSolicitacao } from '../../../models';
import { STATUS_SOLICITACAO_LABELS } from '../solicitacoesListPage.definitions';
import type { ColunaCustomizada } from './solicitacoesKanban.types';

const STATUS_DISPONIVEIS: StatusSolicitacao[] = ['aberta', 'em_andamento', 'concluida', 'cancelada', 'excluida'];

interface SolicitacoesKanbanColunasModalProps {
  isOpen: boolean;
  onClose: () => void;
  colunasIniciais: ColunaCustomizada[];
  /** Valores de alerta/pendência presentes nas Solicitações carregadas agora — não é um
   * catálogo fixo (evita ficar desatualizado se os textos de alerta/pendência mudarem no
   * código), só o que existe pra escolher de fato hoje. */
  opcoesAlerta: string[];
  opcoesPendencia: string[];
  onSalvar: (colunas: ColunaCustomizada[]) => void;
}

/**
 * Configuração do modo "Personalizado" do Kanban — o usuário escolhe livremente quais
 * colunas quer ver, misturando status, alerta e pendência (ex.: só "Gerar Código de
 * Postagem" + "Aguardando Postagem" + status "Novo"). Cada escolha vira uma coluna; a
 * ordem das colunas é a ordem em que foram marcadas aqui.
 */
export default function SolicitacoesKanbanColunasModal({
  isOpen,
  onClose,
  colunasIniciais,
  opcoesAlerta,
  opcoesPendencia,
  onSalvar,
}: SolicitacoesKanbanColunasModalProps) {
  const [selecionadas, setSelecionadas] = useState<ColunaCustomizada[]>(colunasIniciais);

  const estaSelecionado = (tipo: ColunaCustomizada['tipo'], valor: string) =>
    selecionadas.some((c) => c.tipo === tipo && c.valor === valor);

  const alternar = (coluna: ColunaCustomizada) => {
    setSelecionadas((atual) =>
      estaSelecionado(coluna.tipo, coluna.valor)
        ? atual.filter((c) => !(c.tipo === coluna.tipo && c.valor === coluna.valor))
        : [...atual, coluna]
    );
  };

  const handleSalvar = () => {
    onSalvar(selecionadas);
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Personalizar colunas do Kanban"
      maxWidth="max-w-lg"
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={handleSalvar}>
            Salvar colunas ({selecionadas.length})
          </button>
        </div>
      }
    >
      <div className="max-h-[60vh] space-y-4 overflow-y-auto py-4">
        <p className="text-sm text-gray-600">
          Escolha o que cada coluna representa. Uma Solicitação pode aparecer em mais de uma coluna se atender a
          mais de um critério.
        </p>

        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Status</p>
          <div className="space-y-1">
            {STATUS_DISPONIVEIS.map((status) => (
              <label key={status} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={estaSelecionado('status', status)}
                  onChange={() => alternar({ tipo: 'status', valor: status, label: STATUS_SOLICITACAO_LABELS[status] })}
                />
                {STATUS_SOLICITACAO_LABELS[status]}
              </label>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Alerta</p>
          {opcoesAlerta.length === 0 && (
            <p className="text-xs text-gray-400">Nenhum alerta presente nas Solicitações carregadas agora.</p>
          )}
          <div className="space-y-1">
            {opcoesAlerta.map((texto) => (
              <label key={texto} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={estaSelecionado('alerta', texto)}
                  onChange={() => alternar({ tipo: 'alerta', valor: texto, label: texto })}
                />
                {texto}
              </label>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Pendência</p>
          {opcoesPendencia.length === 0 && (
            <p className="text-xs text-gray-400">Nenhuma pendência presente nas Solicitações carregadas agora.</p>
          )}
          <div className="space-y-1">
            {opcoesPendencia.map((label) => (
              <label key={label} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={estaSelecionado('pendencia', label)}
                  onChange={() => alternar({ tipo: 'pendencia', valor: label, label })}
                />
                {label}
              </label>
            ))}
          </div>
        </section>
      </div>
    </BaseModal>
  );
}
